/**
 * Overheads calc — the other half of ./pnl.ts.
 *
 * pnl.ts prices concrete: revenue from pours, cost from materials, labour and
 * trucking. This module prices what it costs to keep the company running, so
 * the dashboard can show net profit rather than concrete margin alone.
 *
 * Everything here is PURE and, per the rule in ./numbers.ts, never throws:
 * every computed value is a finite number, or null where pnl.ts would also
 * return null (a percentage of nothing).
 *
 * The one thing that makes this module different from pnl.ts: the owner types
 * the total AS PRINTED on the statement, tax included, and ticks whether it
 * had GST. Every other figure in this app is entered ex-tax and has tax added
 * to it; here the tax is already inside the number and has to be taken back
 * out. Getting that backwards inflates every overhead by the GST rate.
 *
 * Spec: docs/superpowers/specs/2026-08-26-monthly-expenses-design.md.
 */

import { roundTo } from './numbers';
import type { EquipmentLease, MonthString, OverheadTotals, PnlSummary } from '../types';

function money(value: number): number {
  return roundTo(value, 2) ?? 0;
}

/* ==========================================================================
 * GST, backed out of a tax-included total
 * ======================================================================== */

export interface GstSplit {
  gst_amount: number;
  amount_ex_gst: number;
}

/**
 * The GST inside a total, and what is left once it is out.
 *
 * `amountTotal` is the figure on the statement, tax INCLUDED, so the GST is
 * `total − total / (1 + pct/100)` — not `total × pct/100`, which is the GST on
 * a pre-tax price and would over-state it.
 *
 * With `hadGst` false the whole total is the expense: a line the owner did not
 * tick is not a line whose tax we are entitled to guess at. A 0% rate lands in
 * the same place by arithmetic rather than by branch.
 */
export function backOutGst(amountTotal: number, hadGst: boolean, gstPercent: number): GstSplit {
  const total = money(amountTotal);
  if (!hadGst || gstPercent <= 0) return { gst_amount: 0, amount_ex_gst: total };
  const gst_amount = money(total - total / (1 + gstPercent / 100));
  return { gst_amount, amount_ex_gst: money(total - gst_amount) };
}

/* ==========================================================================
 * Months
 * ======================================================================== */

/** 'YYYY-MM' of an ISO date. Both are fixed-width, so this is a slice. */
export function monthOf(incurredOn: string): MonthString {
  return (incurredOn ?? '').slice(0, 7);
}

/** The first day of a month — the date a lease's tax rate is read on. */
export function firstOfMonth(month: MonthString): string {
  return `${month}-01`;
}

/** The last day of a month, as `YYYY-MM-DD` — day 0 of the next month. */
export function lastOfMonth(month: MonthString): string {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0));
  return `${month}-${String(last.getUTCDate()).padStart(2, '0')}`;
}

/** How many days a month has — the denominator a daily share is taken over. */
export function daysInMonth(month: MonthString): number {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** The month after `month`. 'YYYY-MM' in, 'YYYY-MM' out. */
function nextMonth(month: MonthString): MonthString {
  const [y, m] = month.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

/** What a lease needs to be weighed against a month. */
export type LeaseTerm = Pick<
  EquipmentLease,
  'monthly_amount' | 'had_gst' | 'start_month' | 'end_month' | 'counts_toward_total'
>;

/**
 * Is the lease running in `month`? Both ends are INCLUSIVE: a lease starting
 * in August is paid in August, and one ending in August is paid in August
 * too. Months are 'YYYY-MM', so string comparison IS month comparison.
 */
export function leaseActiveIn(lease: LeaseTerm, month: MonthString): boolean {
  if (lease.start_month > month) return false;
  return lease.end_month === null || lease.end_month === '' || month <= lease.end_month;
}

/**
 * What a lease contributes to a month's overheads, ex-GST — 0 when it is not
 * running that month, or when the owner has cleared "counts toward total" to
 * leave it out of the dashboard for a month, for whatever reason of his own.
 *
 * `gstPercent` is the rate in force on the first of `month`: a lease has no
 * date of its own, so the month being reported is the only date it can be
 * read on.
 */
export function leaseAmountForMonth(
  lease: LeaseTerm,
  month: MonthString,
  gstPercent: number
): number {
  if (!lease.counts_toward_total) return 0;
  if (!leaseActiveIn(lease, month)) return 0;
  return backOutGst(lease.monthly_amount, lease.had_gst === 1, gstPercent).amount_ex_gst;
}

/* ==========================================================================
 * A month's overheads
 * ======================================================================== */

export interface OverheadInputs {
  leases: readonly LeaseTerm[];
}

/**
 * A month's overheads, and the GST paid on them.
 *
 * The Performance Dashboard is the owner's manual book: it totals only what
 * he has typed in himself as a standing monthly cost, so it takes leases and
 * nothing else. A bank line — however it got categorised, however anyone has
 * flagged it — belongs to Total Breakdown, the other book, and never reaches
 * this sum. That used to be enforced by a flag (`is_overhead`) someone had to
 * set correctly on every category; now it is enforced by this function
 * simply never being handed an expense to look at.
 *
 * A lease has no date of its own, so its split is computed here against
 * `gstPercent`, the rate on the first of the month being reported.
 *
 * `expense_gst_paid` is kept apart from `overheads` for the same reason
 * pnl.ts keeps `gst_paid` out of `cost`: GST is recoverable, so it is neither
 * profit nor loss, and rolling it into overheads would understate the month.
 * The name is a holdover from when this figure also summed expense GST; it
 * still means "GST paid on this month's overheads."
 */
export function monthlyOverheads(
  inputs: OverheadInputs,
  month: MonthString,
  gstPercent: number
): OverheadTotals {
  let overheads = 0;
  let gst = 0;
  for (const lease of inputs.leases) {
    if (!lease.counts_toward_total) continue;
    if (!leaseActiveIn(lease, month)) continue;
    const split = backOutGst(lease.monthly_amount, lease.had_gst === 1, gstPercent);
    overheads += split.amount_ex_gst;
    gst += split.gst_amount;
  }
  return { overheads: money(overheads), expense_gst_paid: money(gst) };
}

/**
 * The share of the standing monthly costs a run of calendar days carries —
 * how a lease reaches the Day and Week scopes without pretending it belongs
 * to a Tuesday. Each day inside `[start, end]` (inclusive, ISO 'YYYY-MM-DD')
 * carries `month's overheads ÷ days in that month`, so a range spanning a
 * month end takes August's share at August's rate and September's at
 * September's, and the shares of every day in a month sum back to exactly
 * that month's `monthlyOverheads` — a whole-month range and `monthlyOverheads`
 * itself agree to the cent by construction (modulo the final rounding).
 *
 * `gstPercentFor` resolves the GST rate for a month the same way
 * `monthlyOverheads`'s own caller does — the rate in force on the first of
 * that month — because a lease still has no date of its own.
 *
 * A reversed range contributes nothing rather than throwing, per the rule in
 * ./numbers.ts.
 */
export function overheadsForRange(
  inputs: OverheadInputs,
  start: string,
  end: string,
  gstPercentFor: (month: MonthString) => number
): OverheadTotals {
  let overheads = 0;
  let gst = 0;
  if (start <= end) {
    const DAY_MS = 86_400_000;
    const utc = (date: string): number => {
      const [y, m, d] = date.split('-').map(Number);
      return Date.UTC(y, m - 1, d);
    };
    for (let month = monthOf(start); month <= monthOf(end); month = nextMonth(month)) {
      const overlapStart = start > firstOfMonth(month) ? start : firstOfMonth(month);
      const overlapEnd = end < lastOfMonth(month) ? end : lastOfMonth(month);
      const overlapDays = Math.round((utc(overlapEnd) - utc(overlapStart)) / DAY_MS) + 1;
      if (overlapDays <= 0) continue;
      const monthTotals = monthlyOverheads(inputs, month, gstPercentFor(month));
      const share = overlapDays / daysInMonth(month);
      overheads += monthTotals.overheads * share;
      gst += monthTotals.expense_gst_paid * share;
    }
  }
  return { overheads: money(overheads), expense_gst_paid: money(gst) };
}

/**
 * A month's concrete totals with the GST paid on its overheads folded in.
 *
 * That GST is recoverable in exactly the way GST on materials and freight is,
 * so it belongs in the same figure the owner remits against. It touches
 * `gst_paid` and nothing else: rolling it into `cost` would charge the month
 * for money it gets back.
 */
export function withExpenseGst(summary: PnlSummary, totals: OverheadTotals): PnlSummary {
  return { ...summary, gst_paid: money(summary.gst_paid + totals.expense_gst_paid) };
}

/* ==========================================================================
 * Net profit
 * ======================================================================== */

/** Concrete margin less the overheads that month carried. */
export function netProfit(margin: number, overheads: number): number {
  return money(margin - overheads);
}

/**
 * Net profit as a percent of revenue, to one decimal — null when there is no
 * revenue to be a percent OF, exactly as `margin_pct` behaves in pnl.ts. A
 * month that paid its leases and poured nothing has no percentage, and 0%
 * would read as breaking even.
 */
export function netMarginPct(netProfit: number, revenue: number): number | null {
  if (revenue === 0) return null;
  return roundTo((netProfit / revenue) * 100, 1) ?? null;
}
